import { CareerPath, Tier, CareerPathScoreSection } from '@backrs_ui/common-ui';

import {
  CareerQuestion,
  CareerPathMeaning,
  CareerUserResponseSet,
  CareerPathEnjoyment,
  CareerPathRole,
  CareerPathWithScore,
} from '@backrs_ui/grow-ui/src/models/models';

import { calculateSums } from './calculateUserSumsForPaths';
import { shouldExcludeLowFlexibilityFromTop } from './caregiverFlexibility';
import { ScoreCalculationResult } from '../calculateSurveyResults';

type GetCareerPathScores = Readonly<{
  tier: Tier;
  careerPaths: CareerPath[];
  careerPathRoles: CareerPathRole[];
  userResponseSet: CareerUserResponseSet;
  questions: CareerQuestion[];
  careerPathMeanings: CareerPathMeaning[];
  careerPathEnjoyments: CareerPathEnjoyment[];
  careerResults: ScoreCalculationResult;
}>;

export const getCareerPathScores = ({
  careerPaths,
  careerPathRoles,
  userResponseSet,
  careerResults,
  questions,
  careerPathMeanings,
  careerPathEnjoyments,
}: GetCareerPathScores): CareerPathWithScore[] => {
  const scores = careerPaths.map((careerPath) => {
    const shouldSuggestCareerPath = determineIfSuggestCareerPath(careerPath, careerPathRoles || []);

    return {
      careerPath,
      sum: shouldSuggestCareerPath
        ? calculateSums({
            responseSet: userResponseSet,
            questions,
            careerPath,
            careerPathMeanings,
            careerPathEnjoyments,
            careerResults,
          })
        : 0,
    };
  });

  // Sort the scores in descending order
  const sortedScores = scores.sort((a, b) => b.sum - a.sum);

  // Determine if there are more than 10 paths with score over 10
  const moreThan10PathsOver10 = sortedScores.filter((score) => score.sum > 10).length > 10;

  // First, assign 'TOP' section
  const scoredPathsWithTopSection = sortedScores.map((item, index) => {
    const isHigh = index < 10 || (item.sum > 30 && moreThan10PathsOver10);

    // Apply hard rule: no path with 'low' flexibility can be 'high' fit for users who say 'very important'
    const shouldExcludeFromTop = shouldExcludeLowFlexibilityFromTop(
      item.careerPath,
      userResponseSet,
      questions,
    );

    if (isHigh && !shouldExcludeFromTop) {
      const topScoreSection: CareerPathScoreSection = 'TOP';

      return {
        ...item,
        scoreSection: topScoreSection,
      };
    }

    return { ...item, scoreSection: undefined }; // dont assign a score yet for non TOP items
  });

  // Count the number of 'TOP' paths
  const topPathsCount = scoredPathsWithTopSection.filter(
    (item) => item.scoreSection === 'TOP',
  ).length;

  // Assign 'MIDDLE' section to the next 10 paths after 'TOP' paths and any path with sum > 20
  const allScoredPaths = scoredPathsWithTopSection.map((item, index) => {
    if (item.scoreSection === 'TOP') {
      return item;
    }

    // if the path is within the next 10 after the top paths, or if the sum is over 20, assign 'MIDDLE' section
    const isWithinNext10 = index >= topPathsCount && index < topPathsCount + 10;
    const isSumOver20 = item.sum > 20;

    if (isWithinNext10 || isSumOver20) {
      const middleScoreSection: CareerPathScoreSection = 'MIDDLE';

      return {
        ...item,
        scoreSection: middleScoreSection,
      };
    }

    // otherwise, assign 'LOW' section
    const lowScoreSection: CareerPathScoreSection = 'LOW';

    return { ...item, scoreSection: lowScoreSection };
  });

  return allScoredPaths;
};

/** Helper function to determine if a career path should be suggested based on the user's survey results. We will only make suggestions to users if there are roles associated with the career path in any tier (and currently there are none that are not - so as of now this always returns true). */
const determineIfSuggestCareerPath = (
  careerPath: CareerPath,
  careerPathRoles: CareerPathRole[],
) => {
  const careerPathRolesForPath = careerPathRoles.filter(
    (role) => role.career_path_id === careerPath.id,
  );

  return careerPathRolesForPath.length;
};
